import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Users, Loader2, ShieldAlert, Crown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const { t, dir } = useI18n();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/is-admin`).then(r => setIsAdmin(r.data.is_admin))
      .catch(() => setIsAdmin(false)).finally(() => setChecking(false));
  }, [user]);

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    axios.get(`${API}/admin/users?limit=500`)
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  if (checking) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  if (!user) {
    return (
      <div dir={dir} className="min-h-screen flex flex-col items-center justify-center text-zinc-100 px-6">
        <ShieldAlert className="w-10 h-10 text-amber-400 mb-3" />
        <Button onClick={login} className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black">{t("continue_google") || "Continue with Google"}</Button>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div dir={dir} className="min-h-screen flex flex-col items-center justify-center text-zinc-100 px-6 text-center">
        <ShieldAlert className="w-10 h-10 text-red-400 mb-3" />
        <h1 className="text-xl font-bold">{t("admin_only") || "Admin only"}</h1>
        <Button onClick={() => navigate("/")} variant="outline" className="mt-6 rounded-full border-zinc-700">{t("back") || "Back"}</Button>
      </div>
    );
  }

  const filtered = q ? items.filter(u => (u.email || "").toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase())) : items;
  const paid = filtered.filter(u => u.subscription && ["active", "trialing"].includes(u.subscription.status));
  const trial = filtered.filter(u => !u.subscription && u.trial_active);
  const expired = filtered.filter(u => !u.subscription && !u.trial_active);

  return (
    <div dir={dir} className="min-h-screen text-zinc-100 px-4 sm:px-8 py-10">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <Users className="w-6 h-6 text-emerald-400" />
          <h1 className="text-2xl sm:text-3xl font-black">{t("admin_users_title") || "Users"}</h1>
          <span className="text-xs text-zinc-500" dir="ltr">Σ {total}</span>
          <input
            data-testid="user-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={t("search_email_name") || "Search email or name..."}
            className="ms-auto bg-zinc-900 border border-zinc-800 rounded-full px-4 h-9 text-sm w-64 focus:outline-none focus:border-emerald-500/50"
            dir="ltr"
          />
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6 text-center">
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 py-3">
            <div className="text-2xl font-black text-amber-300 tabular-nums" dir="ltr">{paid.length}</div>
            <div className="text-[10px] uppercase tracking-widest text-amber-200/80">{t("subscribers") || "Subscribers"}</div>
          </div>
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 py-3">
            <div className="text-2xl font-black text-emerald-300 tabular-nums" dir="ltr">{trial.length}</div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-200/80">{t("in_trial") || "In trial"}</div>
          </div>
          <div className="rounded-xl border border-zinc-700/50 bg-zinc-500/5 py-3">
            <div className="text-2xl font-black text-zinc-300 tabular-nums" dir="ltr">{expired.length}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">{t("expired") || "Expired"}</div>
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></div>
        ) : (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
            {filtered.length === 0 ? (
              <div className="p-10 text-center text-zinc-500 text-sm">{t("no_users") || "No users match your search."}</div>
            ) : (
              <div className="divide-y divide-zinc-900">
                {filtered.map(u => {
                  const sub = u.subscription;
                  const isPaid = sub && ["active", "trialing"].includes(sub.status);
                  return (
                    <div key={u.user_id} data-testid={`user-row-${u.email}`} className="flex items-center gap-3 px-4 py-3">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-zinc-800 text-zinc-300 flex items-center justify-center font-bold uppercase">
                          {(u.name || u.email || "?")[0]}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-100 truncate flex items-center gap-2">
                          {u.name || u.email?.split("@")[0]}
                          {isPaid && <Crown className="w-3.5 h-3.5 text-amber-400" />}
                          {!isPaid && u.trial_active && <Sparkles className="w-3.5 h-3.5 text-emerald-400" />}
                        </div>
                        <div className="text-[11px] text-zinc-500 truncate" dir="ltr">{u.email}</div>
                      </div>
                      <div className="text-[10px] text-zinc-500 flex flex-col items-end gap-0.5" dir="ltr">
                        <span className={"uppercase " + (u.auth_provider === "password" ? "text-blue-400" : "text-red-400")}>
                          {u.auth_provider === "password" ? "email" : "google"}
                        </span>
                        <span>{u.created_at?.slice(0, 10)}</span>
                      </div>
                      <div className="text-xs w-24 text-end shrink-0">
                        {isPaid ? (
                          <span className="text-amber-300">
                            {sub.lookup_key === "signals_yearly" ? "Yearly" : "Monthly"}
                            <div className="text-[9px] text-zinc-500">{sub.provider}</div>
                          </span>
                        ) : u.trial_active ? (
                          <span className="text-emerald-300">Trial</span>
                        ) : (
                          <span className="text-zinc-500">—</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
